import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  Plus, 
  Trash2, 
  X, 
  Layers, 
  Calendar, 
  User, 
  Users, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  FileText,
  Bookmark,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Upload,
  Paperclip,
  Globe,
  ExternalLink,
  Link2
} from 'lucide-react';
import { getRoleDayPrefix } from '../../../helper/role-helper';

const CATEGORIES = [
  'Web Development',
  'Frontend Development',
  'Backend Development',
  'Full Stack Development',
  'UI/UX Design',
  'Content Writing',
  'SEO',
  'Digital Marketing',
  'AI Solutions',
  'Quality Assurance',
  'General'
];

const PRESET_TEMPLATES = [
  {
    title: 'Build Landing Page Section',
    category: 'Web Development',
    priority: 'High',
    description: 'Implement a modern, fully responsive landing page section using React and TailwindCSS with smooth hover animations.',
    expectedOutput: 'Production-ready component with responsive desktop/mobile view'
  },
  {
    title: 'Fix Mobile Responsiveness',
    category: 'Web Development',
    priority: 'High',
    description: 'Audit and fix viewport overflow, card squishing, and touch target sizing on mobile and tablet screen sizes.',
    expectedOutput: 'Clean mobile layout with 0 horizontal scrollbar issues'
  },
  {
    title: 'Write SEO Blog Article',
    category: 'Content Writing',
    priority: 'Medium',
    description: 'Write an informative, 1500-word SEO-optimized blog article with 5 FAQs, proper H2/H3 hierarchy, meta title, and meta description.',
    expectedOutput: 'Markdown or document draft with verified keyword density',
    blogTitle: '10 Proven Digital Strategies to Scale in 2026',
    keywords: 'digital growth, SEO optimization, AI tools',
    wordCount: '1500 words'
  },
  {
    title: 'Create Meta Title & Description',
    category: 'SEO',
    priority: 'Medium',
    description: 'Write engaging meta titles (under 60 chars) and meta descriptions (150-160 chars) for specified landing pages.',
    expectedOutput: 'Google SERP-ready meta tags list'
  },
  {
    title: 'API Integration & QA Testing',
    category: 'Web Development',
    priority: 'High',
    description: 'Connect frontend form to backend REST endpoint with loading states, input validation, and user toast notifications.',
    expectedOutput: 'Fully functional form submission with error handling'
  }
];

const CreateDailyAssignmentModal = ({ isOpen, onClose, onSuccess, initialUserId = null }) => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [assignTarget, setAssignTarget] = useState('single'); // 'single' | 'department' | 'all'
  const [selectedUser, setSelectedUser] = useState(initialUserId || '');
  const [selectedDept, setSelectedDept] = useState('');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  
  const todayStr = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(todayStr);
  const [internshipDay, setInternshipDay] = useState(1);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Dynamic Task List
  const [tasks, setTasks] = useState([
    {
      title: '',
      category: 'Web Development',
      priority: 'Medium',
      description: '',
      deadline: todayStr,
      expectedOutput: '',
      referenceUrl: '',
      blogTitle: '',
      keywords: '',
      wordCount: '',
      showAdvanced: false
    }
  ]);

  const resetModalState = () => {
    setTasks([
      {
        title: '',
        category: 'Web Development',
        priority: 'Medium',
        description: '',
        deadline: todayStr,
        expectedOutput: '',
        referenceUrl: '',
        blogTitle: '',
        keywords: '',
        wordCount: '',
        showAdvanced: false,
        files: []
      }
    ]);
    setNotes('');
  };

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      if (initialUserId) {
        setSelectedUser(initialUserId);
      }
      resetModalState();
    }
  }, [isOpen, initialUserId]);

  // Auto-calculate internship day when user or date changes
  useEffect(() => {
    if (selectedUser && users.length > 0) {
      const u = users.find(x => x._id === selectedUser);
      if (u && u.joiningDate) {
        const join = new Date(u.joiningDate);
        const sel = new Date(date);
        const diff = Math.floor((sel - join) / (1000 * 60 * 60 * 24)) + 1;
        if (diff > 0) {
          setInternshipDay(diff);
        }
      }
    }
  }, [selectedUser, date, users]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
      const [userRes, projRes] = await Promise.all([
        axios.get(window.API_URL + '/api/portal/admin/users', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(window.API_URL + '/api/portal/admin/projects', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setUsers(userRes.data.filter(u => u.status === 'Active'));
      setProjects(projRes.data || []);
    } catch (err) {
      console.error("Error fetching users or projects:", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAddTask = () => {
    setTasks(prev => [
      ...prev,
      {
        title: '',
        category: 'Web Development',
        priority: 'Medium',
        description: '',
        deadline: date,
        expectedOutput: '',
        referenceUrl: '',
        blogTitle: '',
        keywords: '',
        wordCount: '',
        showAdvanced: false,
        files: []
      }
    ]);
  };

  const handleRemoveTask = (idx) => {
    if (tasks.length === 1) {
      return Swal.fire('Notice', 'At least one task must be in the assignment.', 'info');
    }
    setTasks(prev => prev.filter((_, i) => i !== idx));
  };

  const handleTaskChange = (idx, field, val) => {
    setTasks(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: val };
      return copy;
    });
  };

  const handleApplyTemplate = (idx, template) => {
    setTasks(prev => {
      const copy = [...prev];
      copy[idx] = {
        ...copy[idx],
        title: template.title,
        category: template.category,
        priority: template.priority,
        description: template.description,
        expectedOutput: template.expectedOutput || '',
        blogTitle: template.blogTitle || '',
        keywords: template.keywords || '',
        wordCount: template.wordCount || '',
        showAdvanced: Boolean(template.blogTitle || template.keywords)
      };
      return copy;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validations
    if (assignTarget === 'single' && !selectedUser) {
      return Swal.fire('Select User', 'Please select a team member or intern.', 'warning');
    }
    if (assignTarget === 'department' && !selectedDept) {
      return Swal.fire('Select Department', 'Please select a department.', 'warning');
    }

    const invalidTask = tasks.find(t => !t.title.trim());
    if (invalidTask) {
      return Swal.fire('Missing Title', 'Please give every task a clear title.', 'warning');
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');

      // Upload any staged files per task
      const processedTasks = await Promise.all(
        tasks.map(async (t) => {
          let attachments = [];
          if (t.files && t.files.length > 0) {
            const formData = new FormData();
            t.files.forEach(f => formData.append('files', f));
            try {
              const uploadRes = await axios.post(`${window.API_URL}/api/portal/admin/upload`, formData, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (uploadRes.data?.filenames) {
                attachments = uploadRes.data.filenames;
              }
            } catch (upErr) {
              console.error("Error uploading task attachments:", upErr);
            }
          }

          return {
            title: t.title.trim(),
            category: t.category,
            priority: t.priority,
            description: (t.description && t.description.trim()) ? t.description.trim() : t.title.trim(),
            deadline: t.deadline || date || new Date().toISOString().split('T')[0],
            expectedOutput: t.expectedOutput,
            referenceUrl: t.referenceUrl,
            attachments,
            blogTitle: t.blogTitle,
            keywords: t.keywords,
            wordCount: t.wordCount
          };
        })
      );

      const payload = {
        date,
        internshipDay: Number(internshipDay) || 1,
        notes,
        project: selectedProject || null,
        tasks: processedTasks
      };

      if (assignTarget === 'single') {
        payload.userIds = [selectedUser];
      } else if (assignTarget === 'department') {
        payload.department = selectedDept;
      } else {
        payload.userIds = users.map(u => u._id);
      }

      const res = await axios.post(window.API_URL + '/api/portal/admin/daily-assignments', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      Swal.fire({
        icon: 'success',
        title: 'Assignment Created!',
        text: res.data.message || `Successfully assigned ${tasks.length} task(s).`,
        confirmButtonColor: '#f06600'
      });

      if (onSuccess) onSuccess();
      resetModalState();
      onClose();
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to create daily assignment', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // Group departments
  const departments = Array.from(new Set(users.map(u => u.fieldOrDepartment).filter(Boolean)));

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-5 overflow-y-auto">
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden my-auto animate-in fade-in zoom-in-95 duration-200">
        
        {/* Modal Header */}
        <div className="px-6 py-5 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-between shrink-0 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#f06600] flex items-center justify-center text-white shadow-lg shadow-orange-500/30">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-black text-white tracking-tight">
                Create Daily Assignment
              </h3>
              <p className="text-xs text-slate-300">
                Assign multiple tasks to one person or department for a specific working day
              </p>
            </div>
          </div>
          <button
            onClick={() => { resetModalState(); onClose(); }}
            className="w-9 h-9 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Target User & Date Configuration Strip */}
          <div className="bg-slate-50 border border-slate-200/90 rounded-2xl p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                <User className="w-4 h-4 text-[#f06600]" /> 1. Target Recipient &amp; Day
              </span>
              
              {/* Assignment Mode Switcher */}
              <div className="flex items-center gap-1 bg-white border border-slate-200 p-1 rounded-xl text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => setAssignTarget('single')}
                  className={`px-3 py-1 rounded-lg transition-all ${assignTarget === 'single' ? 'bg-[#f06600] text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`}
                >
                  Single User
                </button>
                <button
                  type="button"
                  onClick={() => setAssignTarget('department')}
                  className={`px-3 py-1 rounded-lg transition-all ${assignTarget === 'department' ? 'bg-[#f06600] text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`}
                >
                  Department / Team
                </button>
                <button
                  type="button"
                  onClick={() => setAssignTarget('all')}
                  className={`px-3 py-1 rounded-lg transition-all ${assignTarget === 'all' ? 'bg-[#f06600] text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`}
                >
                  All Active
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* User / Dept Selector */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  {assignTarget === 'single' ? 'Select Member *' : assignTarget === 'department' ? 'Select Department *' : 'All Active Users'}
                </label>
                {assignTarget === 'single' ? (
                  <select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    required
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#f06600]/30 focus:border-[#f06600]"
                  >
                    <option value="">-- Choose Member / Intern --</option>
                    {users.map(u => (
                      <option key={u._id} value={u._id}>
                        {u.fullName} ({u.role}{u.fieldOrDepartment ? ` - ${u.fieldOrDepartment}` : ''})
                      </option>
                    ))}
                  </select>
                ) : assignTarget === 'department' ? (
                  <select
                    value={selectedDept}
                    onChange={(e) => setSelectedDept(e.target.value)}
                    required
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#f06600]/30 focus:border-[#f06600]"
                  >
                    <option value="">-- Choose Department --</option>
                    {departments.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                ) : (
                  <div className="px-3 py-2.5 bg-orange-50 border border-orange-200 text-xs font-semibold text-[#f06600] rounded-xl">
                    Assigning to all {users.length} active users
                  </div>
                )}
              </div>

              {/* Working Date */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-slate-500" /> Working Date *
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                  className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#f06600]/30 focus:border-[#f06600]"
                />
              </div>

              {/* Day Number */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-slate-500" /> {(() => {
                    const selUserObj = users.find(u => u._id === selectedUser);
                    return selUserObj ? `${getRoleDayPrefix(selUserObj)} Day # *` : 'Work / Day # *';
                  })()}
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-2.5 text-xs font-black text-[#f06600]">
                    Day
                  </span>
                  <input
                    type="number"
                    min="1"
                    value={internshipDay}
                    onChange={(e) => setInternshipDay(e.target.value)}
                    required
                    placeholder="e.g. 7"
                    className="w-full bg-white border border-slate-300 rounded-xl pl-12 pr-3 py-2.5 text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#f06600]/30 focus:border-[#f06600]"
                  />
                </div>
              </div>

              {/* Project (Optional) */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1">
                  <Layers className="w-3.5 h-3.5 text-[#f06600]" /> Client Project (Optional)
                </label>
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#f06600]/30 focus:border-[#f06600]"
                >
                  <option value="">-- General / No Project --</option>
                  {projects.map(p => (
                    <option key={p._id} value={p._id}>
                      {p.title} {p.clientName ? `(${p.clientName})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Task List Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-black uppercase tracking-wider text-slate-900 flex items-center gap-2">
                  <span>2. Tasks for this Day</span>
                  <span className="w-5 h-5 rounded-full bg-orange-100 text-[#f06600] text-xs flex items-center justify-center font-bold">
                    {tasks.length}
                  </span>
                </h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  Each task will be tracked independently with its own status, submission, and review.
                </p>
              </div>

              <button
                type="button"
                onClick={handleAddTask}
                className="bg-orange-50 hover:bg-orange-100 text-[#f06600] border border-orange-200 font-bold px-3.5 py-2 rounded-xl text-xs flex items-center gap-1.5 transition-colors shadow-sm cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>Add Another Task</span>
              </button>
            </div>

            {/* Task Cards */}
            <div className="space-y-4">
              {tasks.map((task, idx) => (
                <div 
                  key={idx} 
                  className="bg-white border-2 border-slate-200 hover:border-orange-300 rounded-2xl p-4 sm:p-5 transition-all shadow-sm space-y-3 relative group"
                >
                  {/* Task Header bar */}
                  <div className="flex items-center justify-between gap-3 flex-wrap border-b border-slate-100 pb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-lg bg-slate-900 text-white font-bold text-xs flex items-center justify-center">
                        #{idx + 1}
                      </span>
                      <span className="text-xs font-bold text-slate-600">
                        Task Item
                      </span>
                    </div>

                    {/* Quick Template Picker */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="relative">
                        <select
                          onChange={(e) => {
                            const tmpl = PRESET_TEMPLATES[Number(e.target.value)];
                            if (tmpl) handleApplyTemplate(idx, tmpl);
                          }}
                          defaultValue=""
                          className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1 text-xs text-slate-700 font-medium hover:border-orange-300 transition-colors cursor-pointer"
                        >
                          <option value="" disabled>⚡ Load Template...</option>
                          {PRESET_TEMPLATES.map((tmpl, tIdx) => (
                            <option key={tIdx} value={tIdx}>{tmpl.title} ({tmpl.category})</option>
                          ))}
                        </select>
                      </div>

                      {/* Remove Task Button */}
                      <button
                        type="button"
                        onClick={() => handleRemoveTask(idx)}
                        title="Remove Task"
                        className="w-7 h-7 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Task Main Fields */}
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                    {/* Title */}
                    <div className="sm:col-span-6">
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">
                        Task Title *
                      </label>
                      <input
                        type="text"
                        value={task.title}
                        onChange={(e) => handleTaskChange(idx, 'title', e.target.value)}
                        required
                        placeholder="e.g. Build Landing Page Hero Section"
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-sm font-semibold text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#f06600]/30 focus:border-[#f06600]"
                      />
                    </div>

                    {/* Category */}
                    <div className="sm:col-span-3">
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">
                        Category
                      </label>
                      <select
                        value={task.category}
                        onChange={(e) => handleTaskChange(idx, 'category', e.target.value)}
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-2.5 py-2 text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#f06600]/30 focus:border-[#f06600]"
                      >
                        {CATEGORIES.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>

                    {/* Priority */}
                    <div className="sm:col-span-3">
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">
                        Priority
                      </label>
                      <select
                        value={task.priority}
                        onChange={(e) => handleTaskChange(idx, 'priority', e.target.value)}
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-2.5 py-2 text-xs font-bold text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#f06600]/30 focus:border-[#f06600]"
                      >
                        <option value="High">🔴 High Priority</option>
                        <option value="Medium">🟡 Medium Priority</option>
                        <option value="Low">🟢 Low Priority</option>
                      </select>
                    </div>
                  </div>

                  {/* Description / Instructions */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">
                      Instructions &amp; Requirements
                    </label>
                    <textarea
                      rows={2}
                      value={task.description}
                      onChange={(e) => handleTaskChange(idx, 'description', e.target.value)}
                      placeholder="Detailed instructions, requirements, deliverables, or specifications..."
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#f06600]/30 focus:border-[#f06600] leading-relaxed"
                    />
                  </div>

                  {/* Reference URLs / External Links */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1 flex items-center justify-between">
                      <span className="flex items-center gap-1">
                        <Globe className="w-3.5 h-3.5 text-[#f06600]" /> Reference Links &amp; Resources (Figma, GitHub, Drive, Docs, etc.)
                      </span>
                      <span className="text-[10px] text-slate-400 font-normal">Optional</span>
                    </label>
                    <input
                      type="text"
                      value={task.referenceUrl || ''}
                      onChange={(e) => handleTaskChange(idx, 'referenceUrl', e.target.value)}
                      placeholder="e.g. https://figma.com/design/... , https://drive.google.com/... (can separate multiple links with commas or spaces)"
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#f06600]/30 focus:border-[#f06600]"
                    />
                  </div>

                  {/* Attach Files / Documents */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1 flex items-center justify-between">
                      <span className="flex items-center gap-1">
                        <Paperclip className="w-3.5 h-3.5 text-[#f06600]" /> Attach Files / Briefs / Specs to Share (Optional)
                      </span>
                      {task.files && task.files.length > 0 && (
                        <span className="text-[#f06600] font-bold text-[10px]">
                          {task.files.length} file(s) attached
                        </span>
                      )}
                    </label>

                    <div className="relative border border-dashed border-slate-300 hover:border-[#f06600] bg-slate-50 hover:bg-orange-50/20 rounded-xl p-3 text-center transition-all cursor-pointer group">
                      <input
                        type="file"
                        multiple
                        onChange={(e) => {
                          const newFiles = Array.from(e.target.files);
                          handleTaskChange(idx, 'files', [...(task.files || []), ...newFiles]);
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <div className="flex items-center justify-center gap-2 pointer-events-none text-slate-500 text-xs">
                        <Upload className="w-4 h-4 text-[#f06600] group-hover:scale-110 transition-transform" />
                        <span className="font-semibold text-slate-700">Click or drag files here to attach to this task</span>
                        <span className="text-[10px] text-slate-400">(Images, PDFs, ZIPs, Docs)</span>
                      </div>
                    </div>

                    {/* Staged files chip list */}
                    {task.files && task.files.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {task.files.map((file, fIdx) => (
                          <span
                            key={fIdx}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-[11px] font-semibold text-slate-700 shadow-2xs"
                          >
                            <Paperclip className="w-3 h-3 text-[#f06600] shrink-0" />
                            <span className="truncate max-w-[160px]">{file.name}</span>
                            <span className="text-[9px] text-slate-400">({(file.size / 1024).toFixed(0)}KB)</span>
                            <button
                              type="button"
                              onClick={() => {
                                const filtered = task.files.filter((_, i) => i !== fIdx);
                                handleTaskChange(idx, 'files', filtered);
                              }}
                              className="text-slate-400 hover:text-red-500 font-bold ml-1 cursor-pointer"
                              title="Remove file"
                            >
                              ✕
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Toggle Advanced / Specialized Fields */}
                  <div>
                    <button
                      type="button"
                      onClick={() => handleTaskChange(idx, 'showAdvanced', !task.showAdvanced)}
                      className="text-[11px] font-bold text-[#f06600] hover:text-orange-700 flex items-center gap-1 transition-colors cursor-pointer"
                    >
                      {task.showAdvanced ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      <span>{task.showAdvanced ? 'Hide Specialized Requirements' : '+ Add Specialized Requirements (SEO, Content, Deliverable Specs)'}</span>
                    </button>

                    {task.showAdvanced && (
                      <div className="mt-3 p-3.5 bg-orange-50/40 rounded-xl border border-orange-100 grid grid-cols-1 sm:grid-cols-2 gap-3 animate-in fade-in duration-150">
                        <div>
                          <label className="block text-[10px] font-bold text-slate-600 mb-1">Expected Output / Deliverable</label>
                          <input
                            type="text"
                            value={task.expectedOutput || ''}
                            onChange={(e) => handleTaskChange(idx, 'expectedOutput', e.target.value)}
                            placeholder="e.g. Pull request or verified live staging link"
                            className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs text-slate-800"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-600 mb-1">Target Keywords (SEO / Blog)</label>
                          <input
                            type="text"
                            value={task.keywords || ''}
                            onChange={(e) => handleTaskChange(idx, 'keywords', e.target.value)}
                            placeholder="e.g. react native, mobile app"
                            className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs text-slate-800"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-[10px] font-bold text-slate-600 mb-1">Word Count / Deliverable Scope</label>
                          <input
                            type="text"
                            value={task.wordCount || ''}
                            onChange={(e) => handleTaskChange(idx, 'wordCount', e.target.value)}
                            placeholder="e.g. 1500 words or 3 screen mockups"
                            className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs text-slate-800"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              ))}
            </div>
          </div>

          {/* Footer Notes */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              General Daily Assignment Notes (Optional)
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Focus on quality and test all breakpoints before submission"
              className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#f06600]/30 focus:border-[#f06600]"
            />
          </div>

          {/* Modal Actions */}
          <div className="pt-4 border-t border-slate-200 flex items-center justify-between flex-wrap gap-3">
            <div className="text-xs text-slate-500 font-medium">
              Ready to assign <strong>{tasks.length}</strong> tasks for <strong>{(() => {
                const selUserObj = users.find(u => u._id === selectedUser);
                const prefix = selUserObj ? getRoleDayPrefix(selUserObj) : '';
                return `${prefix ? prefix + ' ' : ''}Day ${internshipDay}`;
              })()}</strong>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => { resetModalState(); onClose(); }}
                disabled={saving}
                className="px-5 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 text-xs font-bold transition-colors cursor-pointer"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2.5 rounded-xl bg-[#f06600] hover:bg-orange-600 text-white text-xs font-bold shadow-lg shadow-orange-500/25 transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                    <span>Assigning Tasks...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Create &amp; Assign Tasks</span>
                  </>
                )}
              </button>
            </div>
          </div>

        </form>

      </div>
    </div>
  );
};

export default CreateDailyAssignmentModal;
